import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ChevronRightIcon,
  ChevronDownIcon,
  ArrowPathIcon,
  AcademicCapIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { adminService } from '../services/admin'
import type {
  CurriculumLearningAreaSummary,
  CurriculumStrandSummary,
  CurriculumOutcomeDetail,
  CurriculumRubricIndicatorDetail,
} from '../services/admin'

const statusTone: Record<string, string> = {
  draft: 'bg-warning-100 text-warning-700',
  active: 'bg-success-100 text-success-700',
  superseded: 'bg-stone-100 text-stone-700',
}

const bloomColors: Record<string, string> = {
  remember: 'bg-stone-100 text-stone-700',
  understand: 'bg-info-100 text-info-700',
  apply: 'bg-mint-100 text-mint-700',
  analyze: 'bg-clay-100 text-clay-700',
  evaluate: 'bg-warning-100 text-warning-700',
  create: 'bg-success-100 text-success-700',
}

const rubricLevelColors: Record<string, string> = {
  exceeds: 'bg-success-50 border-success-200',
  meets: 'bg-info-50 border-info-200',
  approaches: 'bg-warning-50 border-warning-200',
  below: 'bg-error-50 border-error-200',
}

export default function CurriculumViewerPage() {
  const [chosenEditionId, setSelectedEditionId] = useState('')
  const [selectedSubStrandId, setSelectedSubStrandId] = useState('')
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set())
  const [expandedStrands, setExpandedStrands] = useState<Set<string>>(new Set())
  const [gradeFilter, setGradeFilter] = useState('')

  const { data: editions, isLoading: editionsLoading } = useQuery({
    queryKey: ['curriculum-v2', 'editions'],
    queryFn: () => adminService.listCurriculumV2Editions({ limit: 100 }),
  })

  // Opens on the live edition: the one teachers see, and usually the only one.
  const selectedEditionId = chosenEditionId || editions?.find((ed) => ed.status === 'active')?.id || ''

  const { data: tree, isLoading: treeLoading, isError: treeError } = useQuery({
    queryKey: ['curriculum-v2', 'tree', selectedEditionId, gradeFilter],
    queryFn: () =>
      adminService.getCurriculumV2EditionTree(selectedEditionId, {
        grade: gradeFilter || undefined,
      }),
    enabled: !!selectedEditionId,
  })

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['curriculum-v2', 'stats', selectedEditionId],
    queryFn: () => adminService.getCurriculumV2EditionStats(selectedEditionId),
    enabled: !!selectedEditionId,
  })

  const { data: subStrandDetail, isLoading: detailLoading, isError: detailError } = useQuery({
    queryKey: ['curriculum-v2', 'sub-strand', selectedSubStrandId],
    queryFn: () => adminService.getCurriculumV2SubStrandDetail(selectedSubStrandId),
    enabled: !!selectedSubStrandId,
  })

  const toggleArea = (id: string) => {
    setExpandedAreas((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleStrand = (id: string) => {
    setExpandedStrands((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Collect unique grades from tree
  const grades = tree
    ? [...new Set(tree.learning_areas.map((a) => a.grade))].sort()
    : []

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="font-outfit text-h1 text-stone-800">Curriculum library</h1>
        <p className="font-inter text-body text-stone-500 mt-1">
          Browse and QA extracted curriculum graph data by edition.
        </p>
      </div>

      {/* Edition selector + grade filter */}
      <div className="flex flex-wrap items-end gap-4">
        <label className="block flex-1 min-w-[240px]">
          <span className="font-inter text-body-sm text-stone-600">Edition</span>
          <select
            className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 font-inter text-body-sm"
            value={selectedEditionId}
            onChange={(e) => {
              setSelectedEditionId(e.target.value)
              setSelectedSubStrandId('')
              setExpandedAreas(new Set())
              setExpandedStrands(new Set())
              setGradeFilter('')
            }}
          >
            <option value="">Select an edition...</option>
            {(editions ?? []).map((ed) => (
              <option key={ed.id} value={ed.id}>
                {ed.title} ({ed.version_tag}) — {ed.status}
              </option>
            ))}
          </select>
        </label>

        {selectedEditionId && grades.length > 1 && (
          <label className="block min-w-[160px]">
            <span className="font-inter text-body-sm text-stone-600">Grade</span>
            <select
              className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 font-inter text-body-sm"
              value={gradeFilter}
              onChange={(e) => setGradeFilter(e.target.value)}
            >
              <option value="">All grades</option>
              {grades.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </label>
        )}

        {selectedEditionId && tree && (
          <span
            className={clsx(
              'inline-flex rounded-full px-3 py-1.5 text-caption font-medium',
              statusTone[tree.status] || 'bg-stone-100 text-stone-700',
            )}
          >
            {tree.status}
          </span>
        )}
      </div>

      {/* Stats cards */}
      {stats && !statsLoading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { label: 'Learning Areas', value: stats.learning_areas },
            { label: 'Strands', value: stats.strands },
            { label: 'Sub-Strands', value: stats.sub_strands },
            { label: 'Outcomes', value: stats.learning_outcomes },
            { label: 'Activities', value: stats.activities },
            { label: 'Competencies', value: stats.competencies },
            { label: 'Rubrics', value: stats.rubrics },
            { label: 'Indicators', value: stats.rubric_indicators },
            { label: 'Rubric Levels', value: stats.rubric_levels },
            { label: 'Metadata', value: stats.metadata_records },
            { label: 'Prerequisites', value: stats.prerequisite_edges },
            { label: 'Embeddings', value: stats.embeddings },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-white rounded-xl border border-stone-100 p-3 shadow-card"
            >
              <p className="font-inter text-caption text-stone-500">{stat.label}</p>
              <p className="font-outfit text-h4 text-stone-800 mt-0.5">{stat.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* No edition selected */}
      {!selectedEditionId && !editionsLoading && (
        <div className="bg-white rounded-2xl border border-stone-100 p-12 shadow-card text-center">
          <AcademicCapIcon className="h-12 w-12 text-stone-300 mx-auto" />
          <p className="font-inter text-body text-stone-500 mt-4">
            Select an edition above to browse the extracted curriculum graph.
          </p>
        </div>
      )}

      {/* Loading */}
      {(treeLoading || editionsLoading) && selectedEditionId && (
        <div className="flex items-center justify-center py-12">
          <ArrowPathIcon className="h-6 w-6 animate-spin text-stone-400" />
          <span className="ml-2 font-inter text-body-sm text-stone-500">Loading...</span>
        </div>
      )}

      {/* Error */}
      {treeError && selectedEditionId && !treeLoading && (
        <div className="bg-error-50 rounded-2xl border border-error-200 p-6 text-center">
          <p className="font-inter text-body-sm text-error-700">
            Failed to load curriculum tree. The edition may have no extracted data yet.
          </p>
        </div>
      )}

      {/* Main content: tree + detail */}
      {tree && !treeLoading && (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Left panel: tree */}
          <div className="xl:col-span-4 bg-white rounded-2xl border border-stone-100 shadow-card overflow-hidden">
            <div className="px-5 py-4 border-b border-stone-100">
              <h2 className="font-outfit text-h4 text-stone-800">Curriculum Tree</h2>
              <p className="font-inter text-caption text-stone-500 mt-0.5">
                {tree.learning_areas.length} learning area{tree.learning_areas.length !== 1 ? 's' : ''}
              </p>
            </div>

            <div className="max-h-[calc(100vh-320px)] overflow-y-auto">
              {tree.learning_areas.length === 0 ? (
                <p className="p-5 font-inter text-body-sm text-stone-500">
                  No content extracted yet.
                </p>
              ) : (
                <div className="py-2">
                  {tree.learning_areas.map((area) => (
                    <LearningAreaNode
                      key={area.id}
                      area={area}
                      expanded={expandedAreas.has(area.id)}
                      expandedStrands={expandedStrands}
                      selectedSubStrandId={selectedSubStrandId}
                      onToggleArea={() => toggleArea(area.id)}
                      onToggleStrand={toggleStrand}
                      onSelectSubStrand={setSelectedSubStrandId}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right panel: detail */}
          <div className="xl:col-span-8">
            {!selectedSubStrandId ? (
              <div className="bg-white rounded-2xl border border-stone-100 p-12 shadow-card text-center">
                <BookOpenIcon className="h-10 w-10 text-stone-300 mx-auto" />
                <p className="font-inter text-body text-stone-500 mt-3">
                  Select a sub-strand from the tree to view its details.
                </p>
              </div>
            ) : detailLoading ? (
              <div className="bg-white rounded-2xl border border-stone-100 p-12 shadow-card flex items-center justify-center">
                <ArrowPathIcon className="h-6 w-6 animate-spin text-stone-400" />
                <span className="ml-2 font-inter text-body-sm text-stone-500">Loading detail...</span>
              </div>
            ) : detailError ? (
              <div className="bg-error-50 rounded-2xl border border-error-200 p-8 text-center">
                <p className="font-inter text-body-sm text-error-700">
                  Failed to load sub-strand detail.
                </p>
              </div>
            ) : subStrandDetail ? (
              <SubStrandDetailPanel detail={subStrandDetail} />
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Tree node components                                                */
/* ------------------------------------------------------------------ */

function LearningAreaNode({
  area,
  expanded,
  expandedStrands,
  selectedSubStrandId,
  onToggleArea,
  onToggleStrand,
  onSelectSubStrand,
}: {
  area: CurriculumLearningAreaSummary
  expanded: boolean
  expandedStrands: Set<string>
  selectedSubStrandId: string
  onToggleArea: () => void
  onToggleStrand: (id: string) => void
  onSelectSubStrand: (id: string) => void
}) {
  return (
    <div>
      <button
        onClick={onToggleArea}
        className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-stone-50 transition-colors text-left"
      >
        {expanded ? (
          <ChevronDownIcon className="h-4 w-4 text-stone-400 shrink-0" />
        ) : (
          <ChevronRightIcon className="h-4 w-4 text-stone-400 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <span className="font-inter text-body-sm font-medium text-stone-800 block truncate">
            {area.name}
          </span>
          <span className="font-inter text-caption text-stone-500">
            {area.grade} &middot; {area.code} &middot; {area.lessons_per_week} les/wk
          </span>
        </div>
        <span className="font-inter text-caption text-stone-400 shrink-0">
          {area.strands.length}
        </span>
      </button>

      {expanded &&
        area.strands.map((strand) => (
          <StrandNode
            key={strand.id}
            strand={strand}
            expanded={expandedStrands.has(strand.id)}
            selectedSubStrandId={selectedSubStrandId}
            onToggle={() => onToggleStrand(strand.id)}
            onSelectSubStrand={onSelectSubStrand}
          />
        ))}
    </div>
  )
}

function StrandNode({
  strand,
  expanded,
  selectedSubStrandId,
  onToggle,
  onSelectSubStrand,
}: {
  strand: CurriculumStrandSummary
  expanded: boolean
  selectedSubStrandId: string
  onToggle: () => void
  onSelectSubStrand: (id: string) => void
}) {
  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 pl-8 pr-4 py-2 hover:bg-stone-50 transition-colors text-left"
      >
        {expanded ? (
          <ChevronDownIcon className="h-3.5 w-3.5 text-stone-400 shrink-0" />
        ) : (
          <ChevronRightIcon className="h-3.5 w-3.5 text-stone-400 shrink-0" />
        )}
        <span className="font-inter text-body-sm text-stone-700 truncate flex-1">
          {strand.number} {strand.name}
        </span>
        <span className="font-inter text-caption text-stone-400 shrink-0">
          {strand.sub_strands.length}
        </span>
      </button>

      {expanded &&
        strand.sub_strands.map((ss) => (
          <button
            key={ss.id}
            onClick={() => onSelectSubStrand(ss.id)}
            className={clsx(
              'w-full flex items-center gap-2 pl-14 pr-4 py-1.5 text-left transition-colors',
              selectedSubStrandId === ss.id
                ? 'bg-teal-50 text-teal-700'
                : 'hover:bg-stone-50 text-stone-600',
            )}
          >
            <span className="font-inter text-caption truncate">
              {ss.number} {ss.name}
            </span>
            {ss.default_term && (
              <span className="font-inter text-caption text-stone-400 shrink-0">
                T{ss.default_term}
              </span>
            )}
          </button>
        ))}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Sub-strand detail panel                                             */
/* ------------------------------------------------------------------ */

function SubStrandDetailPanel({
  detail,
}: {
  detail: NonNullable<ReturnType<typeof adminService.getCurriculumV2SubStrandDetail> extends Promise<infer T> ? T : never>
}) {
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-stone-100 p-5 shadow-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-inter text-caption text-stone-500">
              {detail.learning_area_name} &rsaquo; {detail.strand_name}
            </p>
            <h2 className="font-outfit text-h3 text-stone-800 mt-1">
              {detail.number} {detail.name}
            </h2>
            <p className="font-inter text-body-sm text-stone-500 mt-1">
              {detail.learning_area_grade} &middot; {detail.code}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="font-inter text-caption text-stone-500">
              {detail.suggested_lessons} lessons &middot; {detail.suggested_minutes} min
            </p>
            {detail.default_term && (
              <span className="inline-flex mt-1 rounded-full bg-teal-50 text-teal-700 px-2.5 py-0.5 text-caption font-medium">
                Term {detail.default_term}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Learning Outcomes */}
      {detail.outcomes.length > 0 && (
        <div className="bg-white rounded-2xl border border-stone-100 p-5 shadow-card space-y-4">
          <h3 className="font-outfit text-h4 text-stone-800">
            Learning Outcomes ({detail.outcomes.length})
          </h3>
          {detail.outcomes.map((outcome) => (
            <OutcomeCard key={outcome.id} outcome={outcome} />
          ))}
        </div>
      )}

      {/* Rubric Indicators */}
      {detail.rubric_indicators.length > 0 && (
        <div className="bg-white rounded-2xl border border-stone-100 p-5 shadow-card space-y-4">
          <h3 className="font-outfit text-h4 text-stone-800">
            Assessment Rubric ({detail.rubric_indicators.length} indicator{detail.rubric_indicators.length !== 1 ? 's' : ''})
          </h3>
          {detail.rubric_indicators.map((indicator) => (
            <RubricIndicatorCard key={indicator.id} indicator={indicator} />
          ))}
        </div>
      )}

      {/* Competencies */}
      {detail.competencies.length > 0 && (
        <div className="bg-white rounded-2xl border border-stone-100 p-5 shadow-card space-y-3">
          <h3 className="font-outfit text-h4 text-stone-800">
            Competencies ({detail.competencies.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {detail.competencies.map((comp) => (
              <span
                key={comp.id}
                className="inline-flex items-center rounded-full bg-stone-50 border border-stone-200 px-3 py-1 text-caption text-stone-700"
              >
                <span className="font-medium mr-1.5 capitalize">
                  {comp.competency_type.replace('_', ' ')}:
                </span>
                {comp.description}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Metadata */}
      {detail.metadata && (
        <div className="bg-white rounded-2xl border border-stone-100 p-5 shadow-card space-y-4">
          <h3 className="font-outfit text-h4 text-stone-800">Metadata</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MetadataList label="Key Inquiry Questions" items={detail.metadata.key_inquiry_questions} />
            <MetadataList label="Assessment Methods" items={detail.metadata.assessment_methods} />
            <MetadataList label="Learning Resources" items={detail.metadata.learning_resources} />
            <MetadataList label="Cross-Subject Links" items={detail.metadata.cross_subject_links} />
            <MetadataList label="Non-Formal Activities" items={detail.metadata.non_formal_activities} />
          </div>
        </div>
      )}
    </div>
  )
}

function OutcomeCard({ outcome }: { outcome: CurriculumOutcomeDetail }) {
  return (
    <div className="rounded-xl border border-stone-100 p-4 space-y-3">
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-7 h-7 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center font-outfit text-caption font-bold">
          {outcome.letter}
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-inter text-body-sm text-stone-800">{outcome.outcome_text}</p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="font-inter text-caption text-stone-400">{outcome.code}</span>
            {outcome.bloom_level && (
              <span
                className={clsx(
                  'inline-flex rounded-full px-2 py-0.5 text-caption font-medium capitalize',
                  bloomColors[outcome.bloom_level] || 'bg-stone-100 text-stone-700',
                )}
              >
                {outcome.bloom_level}
              </span>
            )}
            {outcome.difficulty != null && (
              <span className="font-inter text-caption text-stone-400">
                Difficulty: {outcome.difficulty}/5
              </span>
            )}
            {outcome.source_page != null && (
              <span className="font-inter text-caption text-stone-400">
                p.{outcome.source_page}
              </span>
            )}
          </div>
          {outcome.source_excerpt && (
            <p className="font-inter text-caption text-stone-400 mt-1 italic line-clamp-2">
              &ldquo;{outcome.source_excerpt}&rdquo;
            </p>
          )}
        </div>
      </div>

      {/* Activities */}
      {outcome.activities.length > 0 && (
        <div className="pl-10 space-y-1.5">
          <p className="font-inter text-caption font-medium text-stone-500 uppercase tracking-wider">
            Activities
          </p>
          {outcome.activities.map((act) => (
            <div key={act.id} className="flex items-start gap-2">
              <span className="shrink-0 w-1.5 h-1.5 mt-1.5 rounded-full bg-stone-300" />
              <div>
                <p className="font-inter text-caption text-stone-700">{act.description}</p>
                <span className="font-inter text-caption text-stone-400 capitalize">
                  {act.activity_type.replace('_', ' ')}
                </span>
                {act.resources_needed.length > 0 && (
                  <span className="font-inter text-caption text-stone-400">
                    {' '}&middot; {act.resources_needed.join(', ')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function RubricIndicatorCard({ indicator }: { indicator: CurriculumRubricIndicatorDetail }) {
  return (
    <div className="rounded-xl border border-stone-100 p-4 space-y-3">
      <p className="font-inter text-body-sm font-medium text-stone-800">
        {indicator.indicator_text}
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {indicator.levels.map((level) => (
          <div
            key={level.level}
            className={clsx(
              'rounded-lg border p-3',
              rubricLevelColors[level.level] || 'bg-stone-50 border-stone-200',
            )}
          >
            <p className="font-inter text-caption font-medium capitalize mb-1">
              {level.level}
            </p>
            <p className="font-inter text-caption text-stone-600 leading-relaxed">
              {level.description}
            </p>
            <p className="font-inter text-caption text-stone-400 mt-1">
              &ge;{level.min_percentage}%
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

function MetadataList({ label, items }: { label: string; items: string[] }) {
  if (!items.length) return null
  return (
    <div>
      <p className="font-inter text-caption font-medium text-stone-600 mb-1.5">{label}</p>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="shrink-0 w-1.5 h-1.5 mt-1.5 rounded-full bg-stone-300" />
            <span className="font-inter text-caption text-stone-700">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
